import URL from 'url';
import { LEGACY_URL_RE, BOSCH_URL_RE, ImageSize } from './constants';

const TRANSFORMATION_VALUES = ['crop', 'fit'];

const ROTATION_VALUES = ['0', '90', '180', '270'];

export interface ImageOptions {
  size?: ImageSize;
  transformation?: 'crop' | 'fit';
  rotation?: '0' | '90' | '180' | '270';
}

const OWNER_TYPE_MAP = {
  room: 'listing',
  hauser: 'user',
  uni_logo: 'unilogo',
  uni_photo: 'uniphoto',
};

/**
 * Generates transformed image URL using Bosch
 * check https://github.com/housinganywhere/src/blob/master/docs/image-processing-apis.md for specifications.
 * check https://housinganywhere.atlassian.net/wiki/spaces/PROD/pages/1274150954/Image+specs "BE dimensions" section.
 * @param url image URL from platform
 */
// eslint-disable-next-line complexity
export function transformImageUrl(url: string, options: ImageOptions = {}) {
  if (!LEGACY_URL_RE.test(url) && !BOSCH_URL_RE.test(url)) {
    return url;
  }

  const parsedUrl = URL.parse(url);

  let transformation = options?.transformation;
  let rotation = options?.rotation;
  const size = options?.size ?? ImageSize.default;

  let urlOwnerType;
  let urlOwnerId;
  let urlImageSize;
  let urlImageId;

  if (BOSCH_URL_RE.test(url)) {
    // use bosch parameters (t, r, ...) as fallback when an option is not provided.
    if (
      typeof URLSearchParams !== 'undefined' &&
      (!transformation || !rotation) &&
      parsedUrl.search
    ) {
      const urlSearchParams =
        // @ts-ignore
        parsedUrl.searchParams || new URLSearchParams(parsedUrl.search);

      if (!rotation && urlSearchParams.has('r')) {
        const value = urlSearchParams.get('r');

        rotation =
          value && value !== '0' && ROTATION_VALUES.includes(value)
            ? (value as ImageOptions['rotation'])
            : undefined;
      }

      if (!transformation && urlSearchParams.has('t')) {
        const value = urlSearchParams.get('t');

        transformation =
          value && value !== '0' && TRANSFORMATION_VALUES.includes(value)
            ? (value as ImageOptions['transformation'])
            : undefined;
      }
    }

    // extract key information from pathname
    [, , urlOwnerType, urlOwnerId, urlImageSize, urlImageId] = (
      parsedUrl.pathname || ''
    ).split('/');

    if (urlImageSize !== 'default') {
      console.warn(`${url} has already been processed with transformImageUrl`);
    }
  } else {
    // use legacy parameter (rotate, nocrop, ...) as fallback when an option is not provided
    if (
      typeof URLSearchParams !== 'undefined' &&
      (!transformation || !rotation) &&
      parsedUrl.query
    ) {
      const urlSearchParams = new URLSearchParams(parsedUrl.query);

      if (!rotation && urlSearchParams.has('rotate')) {
        const value = urlSearchParams.get('rotate');

        rotation =
          value && value !== '0' && ROTATION_VALUES.includes(value)
            ? (value as ImageOptions['rotation'])
            : undefined;
      }

      if (!transformation && urlSearchParams.has('nocrop')) {
        const value = urlSearchParams.get('nocrop');

        transformation =
          value && value !== '0' && value !== 'false' ? 'fit' : undefined;
      }
    }

    // extract key information from pathname
    const cleanUrl = (parsedUrl.pathname || '')
      .replace('/v2/', '/')
      .replace(/\.(png|jpg|webp)/g, '');

    [, , urlOwnerType, urlOwnerId, urlImageId] = cleanUrl.split('/');

    urlOwnerType = OWNER_TYPE_MAP[urlOwnerType] || urlOwnerType;
  }

  parsedUrl.pathname = `/images/${urlOwnerType}/${urlOwnerId}/${size}/${urlImageId}`;

  if (typeof URLSearchParams !== 'undefined' && (transformation || rotation)) {
    const urlSearchParams = new URLSearchParams('');

    if (transformation) {
      urlSearchParams.set('t', transformation);
    }

    if (rotation) {
      urlSearchParams.set('r', rotation);
    }

    parsedUrl.search = urlSearchParams.toString();
  } else {
    parsedUrl.search = '';
  }

  return URL.format(parsedUrl);
}
